import axios from 'axios'
// import * as Sentry from '@sentry/react'

import { InvalidAPIResponse } from '../../errors'
import config from '../../config'
import { getStoredAccount } from '../../providers/AccountProvider'
import { getAccessToken } from '../..'

//* ~~~~~~ QUERY RELATED ~~~~~~

// General method to convert array to query params
export const arrayToQuery = (name, arr = []) => {
  if (arr.length === 0) {
    return ''
  } else {
    const prefix = `${name}[]=`
    return `${prefix}${arr.join(`&${prefix}`)}`
  }
}

// General method to convert array to query params
export const arrayToQueryEncode = (name, arr = []) => {
  if (arr.length === 0) {
    return ''
  } else {
    const encodedArr = arr.map(x => encodeURIComponent(x))
    const prefix = `${name}[]=`
    return `${prefix}${encodedArr.join(`&${prefix}`)}`
  }
}

// Converts array of embeds to query params
export const embedsToQuery = (embeds = []) => {
  if (embeds.length === 0) {
    return ''
  } else {
    return `?${arrayToQuery('embed', embeds)}`
  }
}

//* ~~~~~~ FILE METHODS ~~~~~~

// Downloads a file from S3 as a blob
export const downloadFromS3 = async signedUrl => {
  if (!signedUrl) {
    return null
  }

  const response = await axios({
    url: signedUrl,
    method: 'GET',
    responseType: 'blob'
  })

  return response?.data || null
}

//* ~~~~~~ HELPER METHODS ~~~~~~

// Wraps a call to the appropriate method in a generic error handler
export const wrapCall = async ({
  method,
  uri,
  data = null,
  rawError = false,
  noData = false
}) => {
  try {
    const response = await method(`${config.apiBaseUri}${uri}`, data)

    if (noData) {
      return response.data
    }

    return response.data?.data ?? null
  } catch (err) {
    if (rawError) {
      throw err
    } else {
      // Sentry.captureMessage(
      //   `${err.message}, details: ${
      //     err?.response?.data?.error_message || 'None'
      //   }`
      // )
      throw new InvalidAPIResponse(err.message, err)
    }
  }
}

// Helper method for GET that sets the correct headers
export const callGet = async uri => {
  return axios.get(uri, await getHeaders())
}

// Helper method for GET that sets the correct headers
export const callGetWithoutAccountId = async uri => {
  return axios.get(uri, await getHeaders(true))
}

// Helper method for POST that sets the correct headers
export const callPost = async (uri, body) => {
  return axios.post(uri, body, await getHeaders())
}

// Helper method for PUT that sets the correct headers
export const callPut = async (uri, body) => {
  return axios.put(uri, body, await getHeaders())
}

// Helper method for PATCH that sets the correct headers
export const callPatch = async (uri, body) => {
  return axios.patch(uri, body, await getHeaders())
}

// Helper method for DELETE that sets the correct headers
export const callDelete = async uri => {
  return axios.delete(uri, await getHeaders())
}

// Helper method for DELETE that sets the correct headers and a body.
export const callDeleteWithBody = async (uri, data) => {
  const headers = await getHeaders()
  return axios.delete(uri, {
    headers: { ...headers.headers },
    data: { ...data }
  })
}

//* ~~~~~~ HEADER CONFIGURATION ~~~~~~

// Helper method for creating the request header
export const getHeaders = async (withoutAccountId = false) => {
  const token = await getAccessToken()
  let account = getStoredAccount()
  const sessionSwitch = localStorage.getItem('session_switch')
  // replacing X-User-Account-Id if switching accounts
  if (sessionSwitch) {
    account = sessionSwitch
  }

  let headers = {}
  if (token) {
    headers = {
      Authorization: `Bearer ${token}`,
      ...headers
    }
  }
  if (account && !withoutAccountId) {
    headers = {
      'X-User-Account-Id': account,
      ...headers
    }
  }
  return { headers }
}
